import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Drawer, Classes, Button, FormGroup, InputGroup, Spinner, Checkbox } from "@blueprintjs/core";
import axios from "axios";
import Cookies from "js-cookie";
import UserListSelector from "./UserListSelector";
import "./manage_access.css";
import { FilledButton } from "../buttons-20250224T111359Z-001/buttons";
import { Tabs } from "@blueprintjs/core";
import { Tab } from "@blueprintjs/core";
import Utilities from "../../utils/Utilities";

const USERS_TABLE_STYLES = `
 .ma-member-tabs .bp3-tab-list { border-bottom: 1px solid #e5e5e5; }
  .ma-member-tabs .bp3-tab { padding: 6px 10px; font-size: 14px; margin-right: 6px; }
  .ma-member-tabs .bp3-tab[aria-selected="true"] { border-bottom-color: #6b21a8; }
  .ma-member-tabs { margin-bottom: 12px;
`;
export default function ManageAccessDrawer({
  isOpen,
  onClose,
  onSubmit,
  initialName = "",
  isSaving = false,
  title = "Manage Team",
  selectedIds = [],
  onMembersChange,
  users = [],
  apiBase,
  editingRoleId = null,
  existingRoleData = null,
  mode = "editRole"
}) {
  const [name, setName] = useState(initialName);
  const [touched, setTouched] = useState(false);
  const [employeeQuery, setEmployeeQuery] = useState("");
  const [teamQuery, setTeamQuery] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modules, setModules] = useState([]);
  const [isLoadingModules, setIsLoadingModules] = useState(false);
  const [moduleStates, setModuleStates] = useState({});
  const [openedModule, setOpenedModule] = useState(null);
  const [teams, setTeams] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [teamError, setTeamError] = useState("");
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);
  const teamAbortControllerRef = useRef(null);
  const visibleTeamIds = useMemo(() => teams.map(team => String(team?.id || "")).filter(Boolean), [teams]);
  const areAllVisibleTeamsSelected = useMemo(() => {
    if (visibleTeamIds.length === 0) return false;
    return visibleTeamIds.every(id => selectedTeamIds.includes(id));
  }, [visibleTeamIds, selectedTeamIds]);

  const isMembersOnly = mode === "editMembers";
  // NEW: Tab state for users drawer (employee / team)
  const [activeTab, setActiveTab] = useState("employee");
  const isSavingState = isSubmitting || isSaving;
  const isNameValid = isMembersOnly ? true : name.trim().length > 0;
  const userSelectorRef = useRef(null);

  const hasSelectedPermission = () => {
    try {
      return Object.values(moduleStates || {}).some(module => module?.enabled && Object.values(module?.permissions || {}).some(Boolean));
    } catch {
      return false;
    }
  };

  const hasAccess = isMembersOnly ? true : hasSelectedPermission();
  const canSave = isMembersOnly ? !isSavingState : !isSavingState && isNameValid && hasAccess;

  const handleSelectorChange = async payload => {
    if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
      if (typeof onMembersChange === "function") onMembersChange?.(payload);
      return;
    }
    const apiUrl = apiBase || Cookies.get("apiEnv");
    if (!apiUrl || !editingRoleId) {
      return;
    }

    const token = Cookies.get("id_token") || Cookies.get("access_token") || Cookies.get("token") || null;
    const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
    const requestData = { role_id: editingRoleId, ...payload };

    try {
      await axios.post(`${apiUrl}/roles/assign_users_to_role`, requestData, { headers });
      let updatedRoles = [];
      try {
        const rolesResponse = await axios.get(`${apiUrl}/roles/roles_list`, { headers });
        updatedRoles = formatRolesList(rolesResponse?.data);
      } catch (error) {
        console.error("Error roles list:", error);
      }
      await onSubmit?.({
        isUpdate: true,
        roleId: editingRoleId,
        users: Array.isArray(requestData.users) ? requestData.users : Array.isArray(selectedIds) ? selectedIds.map(String) : [],
        updatedRoles,
        toastMessage: "Users synced successfully for the role"
      });
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Failed to update users";
      setError(String(message));
    }
  };

  useEffect(() => {
    const fetchModules = async () => {
      const apiUrl = apiBase || Cookies.get("apiEnv");
      if (!apiUrl) return;

      setIsLoadingModules(true);
      try {
        const token = Cookies.get("id_token") || Cookies.get("access_token") || Cookies.get("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;
        const response = await axios.get(`${apiUrl}/roles/module_list`, { headers });
        const responseData = response?.data;

        const modulesList = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.modules)
          ? responseData.modules
          : Array.isArray(responseData?.data)
          ? responseData.data
          : [];

        const formattedModules = modulesList.map((module, index) => {
          const moduleKey = String(module?.module || module?.key || module?.name || module?.module_key || `module_${index}`).toLowerCase();
          const moduleLabel =
            module?.label ||
            module?.display_name ||
            module?.module?.charAt(0)?.toUpperCase() + module?.module?.slice(1) ||
            `Module ${index + 1}`;
          const permissions = Array.isArray(module?.access_types) ? module.access_types : [];

          return {
            key: moduleKey,
            label: moduleLabel,
            permissions: permissions
          };
        });

        setModules(formattedModules);
        const initialState = {};
        formattedModules.forEach(module => {
          const permissionsState = module.permissions.reduce((acc, permission) => {
            acc[permission] = false;
            return acc;
          }, {});
          initialState[module.key] = { enabled: false, permissions: permissionsState };
        });
        setModuleStates(initialState);
      } catch (error) {
        setModules([]);
        setModuleStates({});
      } finally {
        setIsLoadingModules(false);
      }
    };

    if (isOpen && !isMembersOnly) fetchModules();
  }, [isOpen, apiBase, isMembersOnly]);

  useEffect(() => {
    if (isOpen) {
      setName(initialName || "");
      setTouched(false);
      setEmployeeQuery("");
      setTeamQuery("");
      setSelectedTeamIds([]);
      setError("");
      setActiveTab("employee");
    }
  }, [isOpen, initialName]);

  const getRoleAccess = roleData => {
    if (!roleData) return {};

    if (roleData.role_access && typeof roleData.role_access === "object" && !Array.isArray(roleData.role_access)) {
      const result = {};
      Object.keys(roleData.role_access).forEach(key => {
        const moduleKey = String(key).toLowerCase();
        const permissions = getPermissionsArray(roleData.role_access[key]);
        result[moduleKey] = permissions;
      });
      return result;
    }

    if (Array.isArray(roleData.role_access)) {
      const result = {};
      roleData.role_access.forEach(entry => {
        const moduleKey = String(entry?.module || entry?.name || "").toLowerCase();
        if (!moduleKey) return;
        const permissions = getPermissionsArray(entry?.access_types || entry?.access || entry?.permissions || []);
        result[moduleKey] = (result[moduleKey] || []).concat(permissions);
      });
      return result;
    }

    return {};
  };

  const getPermissionsArray = permissions => {
    if (permissions == null) return [];
    if (Array.isArray(permissions)) return permissions.map(perm => String(perm));
    if (typeof permissions === "string") {
      return permissions
        .split(",")
        .map(perm => perm.trim())
        .filter(Boolean);
    }
    if (typeof permissions === "object") {
      const keys = [];
      Object.keys(permissions).forEach(key => {
        const value = permissions[key];
        if (value === true || value === 1) keys.push(String(key));
      });
      return keys;
    }
    return [];
  };

  const hasPermission = (permissions, permission) => {
    if (!Array.isArray(permissions)) return false;
    return permissions.includes(String(permission));
  };

  const normalizeModuleName = name => {
    if (!name) return "";
    return String(name)
      .toLowerCase()
      .replace(/[\s_-]+/g, "");
  };

  useEffect(() => {
    if (!isOpen || isMembersOnly) return;
    if (!modules || modules.length === 0) return;

    const initialState = {};
    modules.forEach(module => {
      const permissionsState = module.permissions.reduce((acc, permission) => {
        acc[permission] = false;
        return acc;
      }, {});
      initialState[module.key] = { enabled: false, permissions: permissionsState };
    });

    if (!editingRoleId || !existingRoleData) {
      setModuleStates(initialState);
      if (existingRoleData && (existingRoleData.name || existingRoleData.role_name)) {
        setName(existingRoleData.name || existingRoleData.role_name || "");
      }
      return;
    }

    const roleAccess = getRoleAccess(existingRoleData);
    const moduleLookup = {};
    modules.forEach(module => {
      const normalizedKey = normalizeModuleName(module.key);
      const normalizedLabel = normalizeModuleName(module.label);
      moduleLookup[normalizedKey] = module;
      moduleLookup[normalizedLabel] = module;
    });

    Object.keys(roleAccess).forEach(moduleKey => {
      const normalizedKey = normalizeModuleName(moduleKey);
      const matchedModule = moduleLookup[normalizedKey];
      if (!matchedModule) {
        return;
      }

      const permissionsArray = getPermissionsArray(roleAccess[moduleKey]);
      const permissionsObject = matchedModule.permissions.reduce((acc, permission) => {
        acc[permission] = hasPermission(permissionsArray, permission);
        return acc;
      }, {});

      initialState[matchedModule.key] = {
        enabled: Object.values(permissionsObject).some(Boolean),
        permissions: permissionsObject
      };
    });

    setModuleStates(initialState);

    if (existingRoleData && (existingRoleData.name || existingRoleData.role_name)) {
      setName(existingRoleData.name || existingRoleData.role_name || "");
    }
  }, [modules, existingRoleData, editingRoleId, isOpen, isMembersOnly]);

  const toggleModuleEnabled = moduleKey => {
    setModuleStates(prev => {
      const isEnabled = !prev[moduleKey]?.enabled;
      setOpenedModule(isEnabled ? moduleKey : openedModule === moduleKey ? null : openedModule);
      return {
        ...prev,
        [moduleKey]: {
          ...prev[moduleKey],
          enabled: isEnabled,
          permissions: isEnabled
            ? prev[moduleKey].permissions
            : Object.keys(prev[moduleKey]?.permissions || {}).reduce((acc, key) => {
                acc[key] = false;
                return acc;
              }, {})
        }
      };
    });
  };

  const toggleModulePermission = (moduleKey, permission) => {
    setTouched(true);
    setOpenedModule(moduleKey);
    setModuleStates(prev => ({
      ...prev,
      [moduleKey]: {
        ...prev[moduleKey],
        enabled: true,
        permissions: { ...prev[moduleKey].permissions, [permission]: !prev[moduleKey].permissions[permission] }
      }
    }));
  };

  const getRoleAccessData = () => {
    const roleAccess = {};
    Object.keys(moduleStates).forEach(moduleKey => {
      const module = moduleStates[moduleKey];
      if (!module.enabled) return;
      const selectedPermissions = Object.keys(module.permissions).filter(permission => !!module.permissions[permission]);
      if (selectedPermissions.length) roleAccess[moduleKey] = selectedPermissions;
    });
    return roleAccess;
  };

  const formatRolesList = data => {
    const rolesList = Array.isArray(data) ? data : data?.roles || data?.data || [];
    if (!Array.isArray(rolesList)) return [];
    return rolesList.map((role, index) => {
      const roleId = role?.id || role?.role_id || `role_${index}`;
      const roleName = role?.name || role?.role_name || role?.display_name || `Role ${index + 1}`;
      const memberIds = Array.isArray(role?.users)
        ? role.users.map(user => String(user?.id || user))
        : Array.isArray(role?.member_ids)
        ? role.member_ids
        : Array.isArray(role?.memberIds)
        ? role.memberIds
        : role?.members
        ? role.members.map(member => member.id?.toString() || member.id)
        : [];
      const actions = role?.actions || role?.permissions || [];
      const description = role?.description || role?.details || "";
      const createdAt = role?.created_at || role?.createdAt || role?.created || null;
      return {
        id: String(roleId),
        name: roleName,
        memberIds,
        actions,
        description,
        created_at: createdAt,
        users: Array.isArray(role?.users) ? role.users : undefined
      };
    });
  };

  const fetchTeamsForTab = useCallback(
    async searchTerm => {
      const apiUrl = apiBase || Cookies.get("apiEnv");
      if (!apiUrl) return;

      try {
        teamAbortControllerRef.current?.abort();
      } catch {
        // ignore abort errors
      }

      const controller = new AbortController();
      teamAbortControllerRef.current = controller;

      setIsLoadingTeams(true);
      setTeamError("");

      const trimmedQuery = (searchTerm || "").trim();

      try {
        const params = new URLSearchParams({
          page: "1",
          per_page: "100"
        });
        if (trimmedQuery) {
          params.append("query", trimmedQuery);
        }
        const response = await axios.get(`${apiUrl}/teams?${params.toString()}`, { signal: controller.signal });
        const teamsList = Array.isArray(response?.data) ? response.data : response?.data?.teams || response?.data?.data || [];
        setTeams(Array.isArray(teamsList) ? teamsList : []);
      } catch (fetchError) {
        if (axios.isCancel?.(fetchError) || fetchError?.name === "CanceledError" || fetchError?.name === "AbortError") {
          return;
        }
        setTeams([]);
        const message = fetchError?.response?.data?.message || fetchError?.message || "Failed to load teams";
        setTeamError(String(message));
      } finally {
        setIsLoadingTeams(false);
      }
    },
    [apiBase]
  );

  useEffect(() => {
    if (!isOpen || !isMembersOnly) return;
    if (activeTab !== "team") return;

    const debounceTimer = setTimeout(() => {
      fetchTeamsForTab(teamQuery);
    }, 400);

    return () => clearTimeout(debounceTimer);
  }, [activeTab, fetchTeamsForTab, isMembersOnly, isOpen, teamQuery]);

  useEffect(() => {
    return () => {
      try {
        teamAbortControllerRef.current?.abort();
      } catch {
        // ignore
      }
    };
  }, []);

  const toggleTeamSelection = teamId => {
    if (!teamId) return;
    const normalizedId = String(teamId);
    setSelectedTeamIds(prev => (prev.includes(normalizedId) ? prev.filter(id => id !== normalizedId) : [...prev, normalizedId]));
  };

  const toggleSelectAllTeams = () => {
    if (visibleTeamIds.length === 0) return;
    if (areAllVisibleTeamsSelected) {
      setSelectedTeamIds(prev => prev.filter(id => !visibleTeamIds.includes(id)));
    } else {
      setSelectedTeamIds(prev => Array.from(new Set([...prev, ...visibleTeamIds])));
    }
  };

  const openTeamInNewTab = team => {
    const id = team?.id ? String(team.id) : null;
    if (!id) return;

    const url = `/teams/${id}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleSave = async () => {
    setTouched(true);
    setError("");

    if (isMembersOnly) {
      setIsSubmitting(true);
      try {
        const apiUrl = apiBase || Cookies.get("apiEnv");
        if (!apiUrl) throw new Error("API base not configured");

        let selectedUsers = [];
        try {
          if (userSelectorRef?.current?.getSelected) {
            selectedUsers = userSelectorRef.current.getSelected();
          }
        } catch (error) {
          selectedUsers = [];
        }

        if (!selectedUsers || (Array.isArray(selectedUsers) && selectedUsers.length === 0)) {
          selectedUsers = Array.isArray(selectedIds) && selectedIds.length > 0 ? selectedIds.map(String) : [];
        }

        let requestData;
        if (selectedUsers && typeof selectedUsers === "object" && !Array.isArray(selectedUsers)) {
          requestData = { role_id: editingRoleId, ...selectedUsers };
        } else {
          const finalSelected = Array.from(new Set((selectedUsers || []).map(String)));
          const original = Array.isArray(selectedIds) ? selectedIds.map(String) : [];
          const originalSet = new Set(original);
          const finalSet = new Set(finalSelected);

          const added = finalSelected.filter(id => !originalSet.has(String(id))).map(String);
          const removed = original.filter(id => !finalSet.has(String(id))).map(String);

          requestData = {
            role_id: editingRoleId,
            users: finalSelected
          };
          if (added.length) requestData.added = added;
          if (removed.length) requestData.removed = removed;
        }

        const token = Cookies.get("id_token") || Cookies.get("access_token") || Cookies.get("token");
        const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

        await axios.post(`${apiUrl}/roles/assign_users_to_role`, requestData, { headers });
        let updatedRoles = [];
        try {
          const rolesResponse = await axios.get(`${apiUrl}/roles/roles_list`, { headers });
          updatedRoles = formatRolesList(rolesResponse?.data);
        } catch (error) {
          console.error("Assign role error:", error);
        }

        await onSubmit?.({
          isUpdate: true,
          roleId: editingRoleId,
          users: Array.isArray(requestData.users) ? requestData.users : Array.isArray(selectedIds) ? selectedIds.map(String) : [],
          updatedRoles,
          toastMessage: "Users synced successfully for the role"
        });
        onClose?.();
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || "Failed to assign users to role";
        setError(String(message));
      } finally {
        setIsSubmitting(false);
      }
      return;
    }

    if (!isMembersOnly && !hasSelectedPermission()) {
      setError("Please assign at least one module and permission.");
      return;
    }

    setIsSubmitting(true);
    try {
      const apiUrl = apiBase || Cookies.get("apiEnv");
      if (!apiUrl) throw new Error("API base not configured");

      const token = Cookies.get("id_token") || Cookies.get("access_token") || Cookies.get("token") || null;
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      const roleAccess = getRoleAccessData();
      const requestData = {
        role_name: name.trim(),
        role_access: roleAccess
      };

      let apiResponse;
      let updatedRoles = [];

      if (editingRoleId) {
        const response = await axios.put(`${apiUrl}/roles/update/${encodeURIComponent(editingRoleId)}`, requestData, { headers });
        apiResponse = response?.data;

        try {
          const rolesResponse = await axios.get(`${apiUrl}/roles/roles_list`, { headers });
          updatedRoles = formatRolesList(rolesResponse?.data);
        } catch (error) {
          console.error("Role updating Error:", error);
        }

        await onSubmit?.({
          isUpdate: true,
          roleId: editingRoleId,
          name: requestData.role_name,
          role_access: roleAccess,
          apiResponse,
          updatedRoles,
          toastMessage: "Role updated successfully"
        });
      } else {
        const response = await axios.post(`${apiUrl}/roles/create`, requestData, { headers });
        apiResponse = response?.data;

        let createdId = apiResponse?.id || apiResponse?.role_id || apiResponse?.data?.id || apiResponse?.data?.role_id || null;

        try {
          const rolesResponse = await axios.get(`${apiUrl}/roles/roles_list`, { headers });
          updatedRoles = formatRolesList(rolesResponse?.data);
          if (!createdId) {
            const roleByName = updatedRoles.find(role => String(role.name).toLowerCase() === String(requestData.role_name).toLowerCase());
            if (roleByName) createdId = roleByName.id;
          }
        } catch (error) {
          console.error("Role creation error:", error);
        }

        if (!createdId && apiResponse) {
          createdId = apiResponse?.result?.id || apiResponse?.role?.id || apiResponse?.created?.id || null;
        }

        await onSubmit?.({
          isUpdate: false,
          roleId: createdId || null,
          name: requestData.role_name,
          role_access: roleAccess,
          apiResponse,
          updatedRoles,
          toastMessage: "Role created successfully"
        });
      }

      onClose?.();
    } catch (error) {
      const message = error?.response?.data?.message || error?.message || "Failed to save role";
      setError(String(message));
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = e => {
      if (e.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1000
        }}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          width: "45%",
          height: "100vh",
          backgroundColor: "white",
          zIndex: 1001,
          boxShadow: "-2px 0 10px rgba(0,0,0,0.1)",
          display: "flex",
          flexDirection: "column"
        }}
        className="mad-drawer"
      >
        <div className={Classes.DRAWER_HEADER}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
            <div>
              <h3 style={{ margin: 0 }}>
                {isMembersOnly ? title || "Manage Users" : title === "Add Role" ? "Add Role" : editingRoleId ? "Edit Role" : "Add Role"}
              </h3>
              {isMembersOnly && existingRoleData && (
                <div style={{ fontSize: "14px", marginTop: "4px" }}>
                  Role: {existingRoleData.name || existingRoleData.role_name || "Unknown Role"}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <FilledButton text={isSavingState ? "Saving…" : "Save"} onClick={handleSave} disabled={!canSave} />
              <Button minimal onClick={onClose}>
                ✕
              </Button>
            </div>
          </div>
        </div>

        <div className={Classes.DRAWER_BODY}>
          <div className={Classes.DIALOG_BODY}>
            {!isMembersOnly && (
              <>
                <FormGroup style={{ fontSize: 14 }} label="Role name" intent={(touched && !isNameValid) || error ? "danger" : "none"}>
                  <InputGroup placeholder="Please enter a role name" value={name} onChange={e => setName(e.target.value)} />
                </FormGroup>
                <div className="ma-modules-wrap">
                  <div className="ma-section-title">Role access</div>
                  <div className="ma-highlight-note">
                    💡 Please select at least one module and enable at least one permission to proceed.
                  </div>

                  {isLoadingModules ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: 20 }}>
                      <Spinner size={16} />
                      <span style={{ marginLeft: 8 }}>Loading modules...</span>
                    </div>
                  ) : modules.length === 0 ? (
                    <div style={{ padding: 16, textAlign: "center", color: "#6b7280" }}>No modules available.</div>
                  ) : (
                    <div className="ma-modules-grid">
                      {modules?.map(module => {
                        const moduleState = moduleStates[module.key] || { enabled: false, permissions: {} };
                        return (
                          <div
                            key={module.key}
                            className={`ma-module-card ${moduleState.enabled ? "is-enabled" : ""} ${
                              openedModule === module.key && moduleState.enabled ? "is-open" : ""
                            }`}
                          >
                            <div className="ma-module-head">
                              <Checkbox
                                className="ma-checkbox"
                                checked={!!moduleState.enabled}
                                onChange={() => toggleModuleEnabled(module.key)}
                              />
                              <div
                                className="ma-module-title"
                                onClick={() => setOpenedModule(openedModule === module.key ? null : module.key)}
                              >
                                {module.label}
                              </div>
                            </div>

                            {moduleState.enabled && (
                              <div className="ma-module-perms">
                                {(module.permissions || []).map(permission => (
                                  <label
                                    key={permission}
                                    className={`ma-perm-pill ${moduleState.permissions[permission] ? "is-checked" : ""}`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={!!moduleState.permissions[permission]}
                                      onChange={() => toggleModulePermission(module.key, permission)}
                                      style={{ cursor: "pointer" }}
                                    />
                                    <span className="ma-perm-label">{permission.charAt(0).toUpperCase() + permission.slice(1)}</span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                <div className="ma-access-card" style={{ marginTop: 16 }} />
              </>
            )}

            {isMembersOnly && (
              <>
                <style>{USERS_TABLE_STYLES}</style>
                <div className="ma-member-tabs">
                  <Tabs
                    id="manage-members-tabs"
                    selectedTabId={activeTab}
                    onChange={id => setActiveTab(id)}
                    renderActiveTabPanelOnly
                    large={false}
                  >
                    <Tab
                      id="employee"
                      title={`Add Users`}
                      panel={
                        <>
                          <div className="table-tab-header">
                            <div>Add Users</div>
                            <div style={{ flex: "0 0 360px", maxWidth: "42%", minWidth: 220 }}>
                              <InputGroup
                                leftIcon="search"
                                placeholder="Search users name or email..."
                                value={employeeQuery}
                                onChange={e => setEmployeeQuery(e.target.value)}
                              />
                            </div>
                          </div>

                          <div style={{ marginTop: 12 }}>
                            <UserListSelector
                              ref={userSelectorRef}
                              users={Array.isArray(existingRoleData?.users) ? existingRoleData.users : []}
                              isOpen={isOpen}
                              apiBase={apiBase}
                              selectedIds={selectedIds}
                              query={employeeQuery}
                              onQueryChange={setEmployeeQuery}
                              onChange={handleSelectorChange}
                              pageSize={100}
                            />
                          </div>
                        </>
                      }
                    />

                    <Tab
                      id="team"
                      title={`Add Teams`}
                      panel={
                        <>
                          <div className="table-tab-header">
                            <div style={{ fontWeight: 600 }}>Add Teams</div>
                            <div style={{ flex: "0 0 360px", maxWidth: "42%", minWidth: 220 }}>
                              <InputGroup
                                leftIcon="search"
                                placeholder="Search team name..."
                                value={teamQuery}
                                onChange={e => setTeamQuery(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="ma-teams-table-wrapper">
                            <table className="ma-teams-table">
                              <thead>
                                <tr style={{ background: "#fafafa" }}>
                                  <th style={{ width: 50, textAlign: "center", padding: "10px 6px", borderBottom: "1px solid #eee" }}>
                                    <Checkbox
                                      className="ma-checkbox"
                                      checked={areAllVisibleTeamsSelected}
                                      onChange={toggleSelectAllTeams}
                                    />
                                  </th>
                                  <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #eee" }}>Teams Name</th>
                                  <th style={{ textAlign: "left", padding: "10px 12px", borderBottom: "1px solid #eee", width: 160 }}>
                                    Users
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {isLoadingTeams ? (
                                  <tr>
                                    <td colSpan={3} style={{ padding: "18px 12px", textAlign: "center" }}>
                                      <Spinner size={18} />
                                    </td>
                                  </tr>
                                ) : teamError ? (
                                  <tr>
                                    <td colSpan={3} style={{ padding: "18px 12px", textAlign: "center", color: "#b91c1c" }}>
                                      {teamError}
                                    </td>
                                  </tr>
                                ) : teams.length === 0 ? (
                                  <tr>
                                    <td colSpan={3} style={{ padding: "18px 12px", textAlign: "center", color: "#6b7280" }}>
                                      No teams found.
                                    </td>
                                  </tr>
                                ) : (
                                  teams?.map(team => {
                                    const teamId = team?.id ? String(team.id) : "";
                                    const isSelected = teamId ? selectedTeamIds.includes(teamId) : false;
                                    return (
                                      <tr key={team.id || Math.random()}>
                                        <td style={{ textAlign: "center", padding: "10px 6px", borderBottom: "1px solid #f1f5f9" }}>
                                          <Checkbox
                                            className="ma-checkbox"
                                            checked={isSelected}
                                            disabled={!teamId}
                                            onChange={() => toggleTeamSelection(team?.id)}
                                          />
                                        </td>
                                        <td style={{ padding: "12px", borderBottom: "1px solid #f1f5f9" }}>
                                          {team?.name || "Untitled team"}
                                        </td>
                                        <td
                                          style={{ padding: "12px", borderBottom: "1px solid #f1f5f9", cursor: "pointer" }}
                                          onClick={() => openTeamInNewTab(team)}
                                        >
                                          <a
                                            href="#"
                                            onClick={e => {
                                              e.preventDefault();
                                            }}
                                            style={{
                                              color: "#1870D5",
                                              textDecoration: "none",
                                              cursor: "pointer",
                                              fontWeight: 500
                                            }}
                                            onMouseEnter={e => (e.target.style.textDecoration = "underline")}
                                            onMouseLeave={e => (e.target.style.textDecoration = "none")}
                                          >
                                            {typeof team?.user_count === "number"
                                              ? `${team.user_count} user${team.user_count === 1 ? "" : "s"}`
                                              : "—"}
                                          </a>
                                        </td>
                                      </tr>
                                    );
                                  })
                                )}
                              </tbody>
                            </table>
                          </div>
                        </>
                      }
                    />
                    <Tabs.Expander />
                  </Tabs>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

ManageAccessDrawer.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  initialName: PropTypes.string,
  isSaving: PropTypes.bool,
  title: PropTypes.string,
  selectedIds: PropTypes.array,
  onMembersChange: PropTypes.func,
  users: PropTypes.array,
  apiBase: PropTypes.string,
  editingRoleId: PropTypes.string,
  existingRoleData: PropTypes.object,
  mode: PropTypes.oneOf(["editRole", "editMembers"])
};
